import { FC, useRef, useState } from 'react';

import { useSession } from '@eneco/auth/useSession';
import RichText from '@eneco/components/RichText/RichText';
import { postEnecoBeXapiSiteApiV1ProfileMergeSessionBySessionIdEmail } from '@eneco/dc-be/client';
import { useRouter } from '@eneco/next';
import { Button, Heading, Text } from '@eneco/sparky';
import { AccountMergeEmailCallbackRendering } from '@sitecore/types/AccountMergeEmailCallback';

import CenteredCardLayout from '../../../components/CenteredCardLayout';
import DataErrorNotification from '../../../components/DataErrorNotification';

const RESEND_BUTTON_COOLDOWN_MS = 5000; // 5 sec

const trimItsmeFromEmail = (email: string | null | undefined) => {
  if (!email) return '';
  return email.replace(/^itsme_/, '');
};

export const AccountMergeEmailCallback: FC<AccountMergeEmailCallbackRendering> = ({ fields }) => {
  const { query } = useRouter();
  const { data: session } = useSession();
  const [isError, setIsError] = useState(false);
  const sessionIdParam = typeof query.sessionId === 'string' ? query.sessionId : undefined;
  const targetEmail = trimItsmeFromEmail(session?.user?.email);

  const [isResending, setIsResending] = useState(false);
  const lastResendTimeRef = useRef<number | null>(null);

  const onResend = async () => {
    const isOnCooldown =
      lastResendTimeRef.current !== null && Date.now() - lastResendTimeRef.current < RESEND_BUTTON_COOLDOWN_MS;
    if (isOnCooldown) return;
    lastResendTimeRef.current = Date.now();

    if (!sessionIdParam || !session?.accessToken) {
      setIsError(true);
      return;
    }
    try {
      setIsResending(true);
      await postEnecoBeXapiSiteApiV1ProfileMergeSessionBySessionIdEmail({
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.accessToken}`,
        },
        path: { sessionId: sessionIdParam },
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <CenteredCardLayout>
      <Heading size="M" as="h1">
        {fields.data.title?.value}
      </Heading>
      <Text as="p" size="BodyM">
        <RichText html={fields.data.introBeforeEmailContent?.value} />
      </Text>
      <Text as="p" size="BodyM" weight="bold">
        {targetEmail}
      </Text>
      <Text as="p" size="BodyM">
        <RichText html={fields.data.instructionsAfterEmailContent?.value} />
      </Text>
      {isError && <DataErrorNotification />}
      <Button type="button" onClick={onResend} isLoading={isResending} isDisabled={isResending}>
        {fields.data.resendButtonLabel?.value}
      </Button>
    </CenteredCardLayout>
  );
};

export default AccountMergeEmailCallback;
